import React, { useState, useRef, useEffect, useMemo } from 'react';
import Avatar from '../../common/Avatar';
import UserSelect from '../../common/UserSelect';
import './TicketFilterBar.scss';

// ── 通用 Dropdown ──
// 專案多的時候一個個找很痛苦 → 下拉內建關鍵字過濾。
// 只過濾「顯示的選項」,不影響已勾選的狀態:搜尋時看不到的項目仍維持原本的勾選。
const FilterSearchBox = ({ value, onChange, placeholder }) => (
  <div className="filter-search">
    <svg width="12" height="12" viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="1.5">
      <circle cx="5.5" cy="5.5" r="3.5" />
      <path d="M8.5 8.5L11 11" strokeLinecap="round" />
    </svg>
    <input
      type="text"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      onClick={(e) => e.stopPropagation()}
    />
    {value && (
      <button type="button" className="filter-search-clear" onClick={() => onChange('')} title="清除">×</button>
    )}
  </div>
);

const FilterDropdown = ({ label, active, children, badge }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    if (open) document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [open]);

  return (
    <div className={`filter-dropdown ${open ? 'open' : ''} ${active ? 'active' : ''}`} ref={ref}>
      <button className="filter-trigger" onClick={() => setOpen(!open)}>
        {label}
        {badge > 0 && <span className="filter-badge">{badge}</span>}
        <svg width="10" height="10" viewBox="0 0 10 10" fill="none" stroke="currentColor" strokeWidth="1.5">
          <path d="M2 4l3 3 3-3" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </button>
      {open && (
        <div className="filter-panel">
          {children}
        </div>
      )}
    </div>
  );
};

const TicketFilterBar = ({
  search,
  onSearchChange,
  statusFilter,
  onStatusFilterChange,
  priorityFilter,
  onPriorityFilterChange,
  assigneeFilter,
  onAssigneeFilterChange,
  projectFilter,
  onProjectFilterChange,
  groupFilter = [],
  onGroupFilterChange,
  groups = [],
  typeFilter = [],
  onTypeFilterChange,
  types = [],
  activeFilterCount,
  onClearAll,
  statusOptions = [],
  companyUsers = [],
  projects = [],
  hideStatusFilter = false,
  hideAssigneeFilter = false,
  searchPlaceholder,
}) => {
  // ── 群組多選（work_item 專用；ticket 不傳 groups 就不顯示）──
  const toggleGroup = (value) => {
    const next = groupFilter.includes(value)
      ? groupFilter.filter((v) => v !== value)
      : [...groupFilter, value];
    onGroupFilterChange(next);
  };
  // ── 類型多選（work_item 專用）──
  const toggleType = (value) => {
    const next = typeFilter.includes(value)
      ? typeFilter.filter((v) => v !== value)
      : [...typeFilter, value];
    onTypeFilterChange(next);
  };
  // ── 狀態多選 ──
  const toggleStatus = (value) => {
    const next = statusFilter.includes(value)
      ? statusFilter.filter((v) => v !== value)
      : [...statusFilter, value];
    onStatusFilterChange(next);
  };

  // ── 優先度多選 ──
  const togglePriority = (value) => {
    const next = priorityFilter.includes(value)
      ? priorityFilter.filter((v) => v !== value)
      : [...priorityFilter, value];
    onPriorityFilterChange(next);
  };

  // 判斷狀態篩選是否偏離預設（全部除了 closed）
  const defaultStatusValues = useMemo(() =>
    statusOptions.filter(s => s.value !== 'closed').map(s => s.value),
    [statusOptions]
  );
  const isStatusCustomized = statusFilter.length > 0 && (
    statusFilter.length !== defaultStatusValues.length ||
    !defaultStatusValues.every(v => statusFilter.includes(v))
  );

  // ── 專案多選 ──
  // 專案 / 群組的下拉關鍵字過濾(只影響顯示,不動勾選狀態)
  const [projectQuery, setProjectQuery] = useState('');
  const [groupQuery, setGroupQuery] = useState('');
  const matchName = (name, q) => !q.trim() || String(name || '').toLowerCase().includes(q.trim().toLowerCase());
  const visibleProjects = projects.filter((p) => matchName(p.name, projectQuery));
  const visibleGroups = groups.filter((g) => matchName(g.name, groupQuery));

  const toggleProject = (projectId) => {
    if (!onProjectFilterChange) return;
    const next = projectFilter.includes(projectId)
      ? projectFilter.filter((v) => v !== projectId)
      : [...projectFilter, projectId];
    onProjectFilterChange(next);
  };

  // 判斷優先度篩選是否偏離預設（全部勾選）
  const ALL_PRIORITIES = [0, 1, 2, 3, 4];
  const isPriorityCustomized = priorityFilter.length !== ALL_PRIORITIES.length ||
    !ALL_PRIORITIES.every(v => priorityFilter.includes(v));


  return (
    <div className="ticket-filter-bar">
      {/* 搜尋框 */}
      <div className="filter-search">
        <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="#999" strokeWidth="2">
          <circle cx="7" cy="7" r="5" />
          <path d="M11 11l3.5 3.5" strokeLinecap="round" />
        </svg>
        <input
          type="text"
          placeholder={searchPlaceholder || "搜尋標題或描述... (#編號 搜尋指定任務)"}
          value={search}
          onChange={(e) => onSearchChange(e.target.value)}
        />
        {search && (
          <button className="search-clear" onClick={() => onSearchChange('')}>
            <svg width="12" height="12" viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M3 3l6 6M9 3l-6 6" strokeLinecap="round" />
            </svg>
          </button>
        )}
      </div>

      {/* 篩選群組 */}
      <div className="filter-group">
        {/* 狀態篩選 */}
        {!hideStatusFilter && (
          <FilterDropdown label="狀態" active={isStatusCustomized} badge={isStatusCustomized ? statusFilter.length : 0}>
            <div className="filter-options">
              {statusOptions.map((s) => (
                <label key={s.value} className="filter-option">
                  <input
                    type="checkbox"
                    checked={statusFilter.includes(s.value)}
                    onChange={() => toggleStatus(s.value)}
                  />
                  <span className="option-dot" style={{ backgroundColor: s.color }} />
                  <span className="option-label">{s.label}</span>
                </label>
              ))}
            </div>
          </FilterDropdown>
        )}

        {/* 類型篩選（work_item 專用） */}
        {types.length > 0 && onTypeFilterChange && (
          <FilterDropdown label="類型" active={typeFilter.length < types.length} badge={types.length - typeFilter.length || 0}>
            <div className="filter-options">
              {types.map((t) => (
                <label key={t.value} className="filter-option">
                  <input type="checkbox" checked={typeFilter.includes(t.value)} onChange={() => toggleType(t.value)} />
                  <span className="option-dot" style={{ backgroundColor: t.color }} />
                  <span className="option-label">{t.label}</span>
                </label>
              ))}
            </div>
          </FilterDropdown>
        )}

        {/* 優先度篩選（多選 checkbox） */}
        <FilterDropdown label="優先度" active={isPriorityCustomized} badge={isPriorityCustomized ? priorityFilter.length : 0}>
          <div className="filter-options">
            {[
              { value: 4, label: '緊急', color: '#dc2626' },
              { value: 3, label: '高', color: '#ef4444' },
              { value: 2, label: '中', color: '#f59e0b' },
              { value: 1, label: '低', color: '#94a3b8' },
              { value: 0, label: '無', color: '#94a3b8' },
            ].map((p) => (
              <label key={p.value} className="filter-option">
                <input
                  type="checkbox"
                  checked={priorityFilter.includes(p.value)}
                  onChange={() => togglePriority(p.value)}
                />
                <span className="priority-dot" style={{ backgroundColor: p.color }} />
                <span className="option-label">{p.label}</span>
              </label>
            ))}
          </div>
        </FilterDropdown>

        {/* 專案篩選 */}
        {projects.length > 0 && onProjectFilterChange && (
          <FilterDropdown label="專案" active={projects.length > 0 && projectFilter?.length < projects.length} badge={projects.length - (projectFilter?.length || 0) || 0}>
            <div className="filter-options">
              {projects.length > 8 && (
                <FilterSearchBox value={projectQuery} onChange={setProjectQuery} placeholder="搜尋專案…" />
              )}
              {/* 有搜尋字時,全選/全不選只作用在「目前顯示的」那幾個,避免誤傷搜尋範圍外的勾選 */}
              <div className="filter-toggle-row">
                <button
                  className="filter-toggle-btn"
                  onClick={() => onProjectFilterChange([...new Set([...(projectFilter || []), ...visibleProjects.map(p => p.id)])])}
                  disabled={visibleProjects.every(p => projectFilter?.includes(p.id))}
                >{projectQuery.trim() ? '選取搜尋結果' : '全選'}</button>
                <button
                  className="filter-toggle-btn"
                  onClick={() => {
                    const drop = new Set(visibleProjects.map(p => p.id));
                    onProjectFilterChange((projectFilter || []).filter(id => !drop.has(id)));
                  }}
                  disabled={!visibleProjects.some(p => projectFilter?.includes(p.id))}
                >{projectQuery.trim() ? '取消搜尋結果' : '全不選'}</button>
              </div>
              {visibleProjects.length === 0 && <div className="filter-empty">找不到符合的專案</div>}
              {visibleProjects.map((p) => (
                <label key={p.id} className="filter-option">
                  <input
                    type="checkbox"
                    checked={projectFilter?.includes(p.id) || false}
                    onChange={() => toggleProject(p.id)}
                  />
                  <span className="option-dot" style={{ backgroundColor: p.color || '#667eea' }} />
                  <span className="option-label">{p.name}</span>
                </label>
              ))}
            </div>
          </FilterDropdown>
        )}

        {/* 群組篩選（work_item 專用；有選=只顯示選的群組） */}
        {groups.length > 0 && onGroupFilterChange && (
          <FilterDropdown label="群組" active={groupFilter.length < groups.length} badge={groups.length - groupFilter.length || 0}>
            <div className="filter-options">
              {groups.length > 8 && (
                <FilterSearchBox value={groupQuery} onChange={setGroupQuery} placeholder="搜尋群組…" />
              )}
              <div className="filter-toggle-row">
                <button
                  className="filter-toggle-btn"
                  onClick={() => onGroupFilterChange([...new Set([...groupFilter, ...visibleGroups.map((g) => g.id)])])}
                  disabled={visibleGroups.every((g) => groupFilter.includes(g.id))}
                >{groupQuery.trim() ? '選取搜尋結果' : '全選'}</button>
                <button
                  className="filter-toggle-btn"
                  onClick={() => {
                    const drop = new Set(visibleGroups.map((g) => g.id));
                    onGroupFilterChange(groupFilter.filter((id) => !drop.has(id)));
                  }}
                  disabled={!visibleGroups.some((g) => groupFilter.includes(g.id))}
                >{groupQuery.trim() ? '取消搜尋結果' : '全不選'}</button>
              </div>
              {visibleGroups.length === 0 && <div className="filter-empty">找不到符合的群組</div>}
              {visibleGroups.map((g) => (
                <label key={g.id} className="filter-option">
                  <input type="checkbox" checked={groupFilter.includes(g.id)} onChange={() => toggleGroup(g.id)} />
                  <span className="option-label">{g.name}</span>
                </label>
              ))}
            </div>
          </FilterDropdown>
        )}

        {/* 指派人篩選（使用 UserSelect：分部門 + 搜尋） */}
        {!hideAssigneeFilter && (
          <div className="filter-assignee-wrapper">
            <UserSelect
              values={assigneeFilter}
              onChange={onAssigneeFilterChange}
              users={companyUsers}
              placeholder="指派人員"
            />
            {assigneeFilter.length > 0 && (
              <span className="filter-assignee-badge">{assigneeFilter.length}</span>
            )}
          </div>
        )}

        {/* 清除篩選 */}
        {activeFilterCount > 0 && (
          <button className="clear-filters-btn" onClick={onClearAll}>
            清除
            <span className="clear-badge">{activeFilterCount}</span>
          </button>
        )}
      </div>
    </div>
  );
};

export default TicketFilterBar;
